import React, { ReactNode, useCallback, useEffect, useState } from 'react'
import {
  SIPMasterPayload,
  InvestPaymentMethodTypes,
  InvestPaymentMethodType,
  SystematicOTPTriggerPayload,
  PaymentPageType,
  SystematicPayload,
  OrderOtpState
} from '../../types/mf-execution.types'
import {
  OTPVerificationState,
  OTPVerificationHandlers,
  OTPVerificationFailedCase as OTPFailedCase
} from '../../types/otp.types'
import { SIPContext } from '../contexts/SIPContext'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createSystematic, fetchUserInvestPaymentMethodsV2, triggerSystematicOTP } from '../../repository/mfExecution'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CalendarSelection, FrequencyType } from '../../types/calendar.types'
import { useMFExecutionContext } from '../contexts/MFExecutionContext'
import { getAvailableMonthlyDays, getSIPDefaultAmount, getSIPMaxAmount, getSIPMinAmount, getSIPStepper } from '../../utils/mf-execution/sipDataUtils'
import { manualDelay } from '../../utils/utils'
import { OTP_VERIFICATION_DELAY } from '../../constants'
import { useInstalledUpiIntents } from '../../hooks/mf-execution/useInstalledUpiIntents'
import { buildOrderOtpPaymentFields } from '../../utils/mf-execution/paymentMethodSelection'
import { isUpiIntentsFlowEnabled } from '../../utils/mf-execution/upiIntentsFeature'
import {
  AmountAdjustmentReason,
  getEffectiveMaxAmount,
  normalizeAllocationAmount,
} from '../../utils/mf-execution/paymentAmount'

interface SIPProviderProps {
  children: ReactNode
}

const OTP_VERIFICATION_LIMIT = 300

const SIPProvider: React.FC<SIPProviderProps> = ({ children }) => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { mfExecutionData } = useMFExecutionContext()
  const [isSIPOTPVerified, setIsSIPOTPVerified] = useState(false)
  const [sipMasterPayload, setSIPMasterPayload] = useState<SIPMasterPayload>({
    paymentMethodId: '',
    paymentMethodType: InvestPaymentMethodTypes.MANDATE,
    transactionType: 'SIP',
    isin: '',
    amount: 0,
    frequencyValue: 1,
    frequencyType: FrequencyType.MONTHLY
  })
  const [amount, setAmount] = useState(0)
  const [initialShownAmount, setInitialShownAmount] = useState<number | null>(null)
  const [fromAdvisory, setFromAdvisory] = useState(() => searchParams.get('fromAdvisory') === 'true')
  const [fromApp, setFromApp] = useState(() => searchParams.get('from') === 'APP')
  const [amountAdjustmentReason, setAmountAdjustmentReason] = useState(AmountAdjustmentReason.NONE)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<InvestPaymentMethodType | null>(null)
  const [calendarSelection, setCalendarSelection] = useState<CalendarSelection>({
    frequencyType: FrequencyType.MONTHLY,
    value: getAvailableMonthlyDays(mfExecutionData?.sip)?.[0]?.day ?? 1
  })
  const [otpVerificationCount, setOtpVerificationCount] = useState(0)
  const [otpReferenceId, setOtpReferenceId] = useState('')
  const [isOTPVerificationLimitExceeded, setIsOTPVerificationLimitExceeded] = useState(false)
  const [OTPVerificationFailedCase, setOTPVerificationFailedCase] = useState<OTPFailedCase | null>(null)
  const [otpState, setOtpState] = useState<OrderOtpState | null>(null)

  const { data: paymentDetails, isPending: isPaymentDetailsPending, refetch: refetchPaymentMethods } = useQuery({
    queryKey: ['user-invest-payment-details'],
    queryFn: fetchUserInvestPaymentMethodsV2
  })

  useEffect(() => {
    setCalendarSelection({
      frequencyType: FrequencyType.MONTHLY,
      value: getAvailableMonthlyDays(mfExecutionData?.sip)?.[0]?.day ?? 1
    })
  }, [mfExecutionData?.sip])

  // Opening amount only. Later frequency changes and keypad edits leave this as-is.
  const rememberInitialShownAmount = (shownAmount: number) => {
    if (shownAmount <= 0) return
    setInitialShownAmount((current) => current ?? shownAmount)
  }

  // Priority: ?amount= (forced, allocation-normalized) > SIP default.
  // Strip only amount after apply; omit searchParams from deps so clearing does not re-default.
  useEffect(() => {
    if (!mfExecutionData?.sip) return

    const amountFromParams = parseFloat(searchParams.get('amount') ?? '0')
    const amountFromEntry =
      Number.isFinite(amountFromParams) && amountFromParams > 0 ? amountFromParams : 0
    const fromAdvisoryEntry = searchParams.get('fromAdvisory') === 'true'
    const fromAppEntry = searchParams.get('from') === 'APP'
    setFromAdvisory(fromAdvisoryEntry)
    setFromApp(fromAppEntry)

    if (amountFromEntry > 0) {
      if (fromAdvisoryEntry && isPaymentDetailsPending) return

      if (fromAdvisoryEntry) {
        const { amount: normalizedAmount, adjustment } = normalizeAllocationAmount(amountFromEntry, {
          minAmount: getSIPMinAmount(mfExecutionData.sip, calendarSelection.frequencyType),
          maxAmount: getEffectiveMaxAmount(
            getSIPMaxAmount(mfExecutionData.sip, calendarSelection.frequencyType),
            paymentDetails?.paymentMethods
          ),
          stepper: getSIPStepper(mfExecutionData.sip, calendarSelection.frequencyType),
        })
        setAmount(normalizedAmount)
        rememberInitialShownAmount(normalizedAmount)
        setAmountAdjustmentReason(adjustment)
      } else {
        setAmount(amountFromEntry)
        rememberInitialShownAmount(amountFromEntry)
        setAmountAdjustmentReason(AmountAdjustmentReason.NONE)
      }

      if (searchParams.has('amount')) {
        const nextParams = new URLSearchParams(searchParams)
        nextParams.delete('amount')
        setSearchParams(nextParams, { replace: true })
      }
      return
    }

    const defaultShownAmount = getSIPDefaultAmount(mfExecutionData.sip, calendarSelection.frequencyType)
    setAmount(defaultShownAmount)
    rememberInitialShownAmount(defaultShownAmount)
  }, [mfExecutionData?.sip, calendarSelection.frequencyType, isPaymentDetailsPending, paymentDetails?.paymentMethods])

  const handleAmountChange = (amountParam: number) => {
    setAmount(amountParam)
  }

  const clearAmountAdjustmentReason = useCallback(() => {
    setAmountAdjustmentReason(AmountAdjustmentReason.NONE)
  }, [])

  const handleCalendarSelection = (calendarSelectionParam: CalendarSelection) => {
    setCalendarSelection(calendarSelectionParam)
  }

  const upiIntentsEnabled = isUpiIntentsFlowEnabled()
  const { installedUpiApps, requestUpiIntents } = useInstalledUpiIntents({
    enabled: upiIntentsEnabled,
    requestOnMount: upiIntentsEnabled,
  })

  const paymentMethods = paymentDetails?.paymentMethods
  const bankAccountDetails = paymentDetails?.bankAccountDetails
  const defaultPaymentMethodDetails = paymentDetails?.defaultPaymentMethodDetails
  const upiIntentMaximumAllowedAmount = paymentDetails?.upiIntentMaximumAllowedAmount ?? 0

  const { mutate: triggerOTPMutate } = useMutation({
    mutationFn: (payload: SystematicOTPTriggerPayload) => triggerSystematicOTP(payload),
    onSuccess: (data) => {
      setOtpReferenceId(data?.otpReferenceId)
    },
    onError: () => {
      console.error('OTP trigger error')
    }
  })

  // This same API is used for OTP Verification and Order Creation
  const { mutate: createOrderMutate, isPending: isOTPVerificationLoading } = useMutation({
    mutationFn: (payload: SystematicPayload) => createSystematic(payload),
    onSuccess: async (data) => {
      console.log('SIP OTP verification successful', data)
      setOtpState(data?.otpState ?? OrderOtpState.UNKNOWN)
      if (data?.isOtpVerified) {
        setIsSIPOTPVerified(true)
        await manualDelay(OTP_VERIFICATION_DELAY)
        const paymentMethodType = sipMasterPayload.paymentMethodType
        if (paymentMethodType === InvestPaymentMethodTypes.NET_BANKING) {
          // Go To NetBanking Loading
          navigate(`${PaymentPageType.SIP}/net-banking/link-waiting?sxpId=${data?.sxpId}`)
        } else if (
          paymentMethodType === InvestPaymentMethodTypes.UPI ||
          paymentMethodType === InvestPaymentMethodTypes.UPI_INTENT
        ) {
          const displayAccNumber = selectedPaymentMethod?.displayAccNumber || ''
          navigate(
            `${PaymentPageType.SIP}/upi/payment-status?sxpId=${data?.sxpId}&displayAccNumber=${encodeURIComponent(displayAccNumber)}`
          )
        } else if (paymentMethodType === InvestPaymentMethodTypes.MANDATE) {
          navigate(`${PaymentPageType.SIP}/order-success`)
        } else {
          navigate(`${PaymentPageType.SIP}/order-success`)
        }
      } else {
        // Handle different OTP states
        if (data?.otpState === OrderOtpState.MAXIMUM_LIMIT_REACHED) {
          setIsOTPVerificationLimitExceeded(true)
        } else if (data?.otpState === OrderOtpState.INVALID_OTP) {
          setOTPVerificationFailedCase(OTPFailedCase.INVALID_OTP)
        } else if (data?.otpState === OrderOtpState.MAXIMUM_INCORRECT_RESPONSE) {
          setIsOTPVerificationLimitExceeded(true)
        } else {
          setOTPVerificationFailedCase(OTPFailedCase.OTP_VERIFICATION_FAILED)
        }
      }
    },
    onError: () => {
      setOtpState(OrderOtpState.UNKNOWN)
      if (selectedPaymentMethod?.type === InvestPaymentMethodTypes.MANDATE) {
        navigate(`${PaymentPageType.SIP}/payment-failed`)
      } else {
        setOTPVerificationFailedCase(OTPFailedCase.OTP_VERIFICATION_FAILED)
      }
    }
  })

  const resetOTPVerificationState = () => {
    setIsOTPVerificationLimitExceeded(false)
    setOTPVerificationFailedCase(null)
    setOtpVerificationCount(0)
    setIsSIPOTPVerified(false)
    setOtpState(null)
  }

  const handleOTPTrigger = (payload: SIPMasterPayload) => {
    resetOTPVerificationState()
    setSIPMasterPayload(payload)

    const otpPaymentFields = buildOrderOtpPaymentFields(
      { type: payload.paymentMethodType, upiPackageName: payload.upiPackageName },
      upiIntentsEnabled
    )

    const otpPayload: SystematicOTPTriggerPayload = {
      paymentMethodId: payload.paymentMethodId,
      paymentMethodType: otpPaymentFields.paymentMethodType,
      transactionType: 'SIP',
      isin: payload?.isin ?? '',
      amount: payload.amount,
      frequencyValue: payload.frequencyValue,
      frequencyType: payload.frequencyType,
      ...(otpPaymentFields.upiPackageName ? { upiPackageName: otpPaymentFields.upiPackageName } : {}),
    }
    triggerOTPMutate(otpPayload)
  }

  const handlePaymentMethodChange = (paymentMethodParam: InvestPaymentMethodType | null) => {
    setSelectedPaymentMethod(paymentMethodParam)
  }

  const handleSIPMasterPayload = (payload: SIPMasterPayload) => {
    setSIPMasterPayload(payload)
  }

  const isOTPValid = (otp: string) => {
    return otp.length === 6 && /^\d{6}$/.test(otp)
  }

  const handleSIPOTPVerification = async (otp: string) => {
    if (!isOTPValid(otp)) {
      setOTPVerificationFailedCase(OTPFailedCase.INVALID_OTP)
      return
    }
    if (otpVerificationCount >= OTP_VERIFICATION_LIMIT) {
      setIsOTPVerificationLimitExceeded(true)
      return
    }
    setOTPVerificationFailedCase(null)
    setOtpVerificationCount(otpVerificationCount + 1)
    createOrderMutate({
      otp: otp,
      otpReferenceId: otpReferenceId,
      sxpDetails: sipMasterPayload
    })
  }

  const handleSIPResendOTP = () => {
    if (sipMasterPayload.paymentMethodId) {
      const otpPaymentFields = buildOrderOtpPaymentFields(
        {
          type: sipMasterPayload.paymentMethodType,
          upiPackageName: sipMasterPayload.upiPackageName,
        },
        upiIntentsEnabled
      )

      const otpPayload: SystematicOTPTriggerPayload = {
        paymentMethodId: sipMasterPayload.paymentMethodId,
        paymentMethodType: otpPaymentFields.paymentMethodType,
        transactionType: 'SIP',
        isin: sipMasterPayload?.isin ?? '',
        amount: sipMasterPayload.amount,
        frequencyValue: sipMasterPayload.frequencyValue,
        frequencyType: sipMasterPayload.frequencyType,
        ...(otpPaymentFields.upiPackageName ? { upiPackageName: otpPaymentFields.upiPackageName } : {}),
      }
      triggerOTPMutate(otpPayload)
    }
  }

  const otpVerificationState: OTPVerificationState = {
    isOTPVerified: isSIPOTPVerified,
    isOTPVerificationLoading,
    isOTPVerificationLimitExceeded,
    OTPVerificationFailedCase
  }

  const otpVerificationHandlers: OTPVerificationHandlers = {
    handleOTPVerification: handleSIPOTPVerification,
    handleResendOTP: handleSIPResendOTP
  }

  return (
    <SIPContext.Provider
      value={{
        sipMasterPayload,
        handleSIPMasterPayload,
        isSIPOTPVerified,
        handleSIPOTPVerification,
        otpVerificationState,
        otpVerificationHandlers,
        paymentMethods,
        bankAccountDetails,
        defaultPaymentMethodDetails,
        upiIntentMaximumAllowedAmount,
        selectedPaymentMethod,
        handlePaymentMethodChange,
        amount,
        initialShownAmount,
        handleAmountChange,
        fromAdvisory,
        fromApp,
        amountAdjustmentReason,
        clearAmountAdjustmentReason,
        calendarSelection,
        handleCalendarSelection,
        handleOTPTrigger,
        handleRefetchPaymentMethods: refetchPaymentMethods,
        otpState,
        installedUpiApps,
        requestUpiIntents
      }}
    >
      {children}
    </SIPContext.Provider>
  )
}

export default SIPProvider
